import { useSelector } from 'react-redux';
import { Box, TableRow, TableCell, Typography, MenuItem } from '@mui/material';
import moment from 'moment';

import { FButtonDropdown, FHTooltip } from '../../base';
import useReadOnlyTimesheetView from '../../../hooks/useReadOnlyTimesheetView';
import { calculateDateDifferenceInHHMM } from '../../../helpers';
import { FORTA_BLUE, FORTA_WHITE } from '../../../utils/colors';
import { THERAPY_HOURS_LABEL } from './utils';
import EllipsisIcon from '../../svgs/EllipsisIcon';
import TrashIcon from '../../svgs/TrashIcon';
import EditOutlinedIcon from '../../svgs/EditOutlinedIcon';

import type { ITimesheetTransformedData } from '../../../utils/types';
import type { RootState } from '../../../store';

interface ITimesheetViewRow {
  timesheet: ITimesheetTransformedData;
  onModeChange: () => void;
  onRemove: () => void;
}

const TableRowView = ({ timesheet, onModeChange, onRemove }: ITimesheetViewRow) => {
  const { id, provider_name, client_name, service_code, date_start, date_end, label, read_only } =
    timesheet;
  const { isAddingTimesheet, editingTimesheetId, isPayPeriodEditable } = useSelector(
    (state: RootState) => state.timesheets,
  );

  const isReadOnlyView = useReadOnlyTimesheetView();

  const DropdownIcon = () => {
    return <EllipsisIcon testId="timeSheetMenuButton" />;
  };
  return (
    <TableRow
      data-testid={`timeSheetRow-${client_name ? client_name.split(' ').join('') : ''}`}
      key={id}
    >
      {isReadOnlyView && <TableCell>{provider_name}</TableCell>}
      <TableCell>{client_name}</TableCell>
      <TableCell>
        {service_code === THERAPY_HOURS_LABEL ? (
          <Typography color={FORTA_BLUE.primary} fontWeight={600}>
            {service_code}
          </Typography>
        ) : (
          <Typography>{service_code}</Typography>
        )}
      </TableCell>
      <TableCell data-testid="dateValueTimeSheetRow">
        {moment(date_start).format('MM/DD/YYYY')}
      </TableCell>
      <TableCell data-testid="startTimeValueTimeSheetRow">
        {moment(date_start).format('h:mm a')}
      </TableCell>
      <TableCell data-testid="endTimeValueTimeSheetRow">
        {moment(date_end).format('h:mm a')}
      </TableCell>
      <TableCell data-testid="durationValueTimeSheetRow">
        {calculateDateDifferenceInHHMM(moment(date_start), moment(date_end))}
      </TableCell>
      <TableCell>
        <Box sx={{ maxWidth: '30vw' }}>
          <FHTooltip
            title={label}
            tooltipbackground={FORTA_WHITE}
            tooltipBoxShadow={`0px 4px 4px rgba(0, 0, 0, 0.25)`}
            tooltipBorder={`2px solid ${FORTA_BLUE.light3}`}
            tooltipBorderRadius="10px"
            placement="bottom-start"
          >
            <Typography noWrap>{label}</Typography>
          </FHTooltip>
        </Box>
      </TableCell>
      <TableCell>
        {!(read_only || isReadOnlyView) && isPayPeriodEditable ? (
          <FButtonDropdown
            buttonRenderer={DropdownIcon}
            disabled={Boolean(isAddingTimesheet || editingTimesheetId)}
            svgHoverStyles={{
              '& > path': {
                stroke: FORTA_WHITE,
              },
            }}
          >
            <MenuItem data-testid="editTimeSheet" onClick={onModeChange}>
              <Box ml={-2.4} sx={{ display: 'flex', alignItems: 'center' }}>
                <EditOutlinedIcon color={FORTA_BLUE.primary} width={16} height={16} />
                <Typography ml={1}>Edit</Typography>
              </Box>
            </MenuItem>
            <MenuItem data-testid="deleteTimeSheet" onClick={onRemove}>
              <Box ml={-2.4} sx={{ display: 'flex', alignItems: 'center' }}>
                <TrashIcon width={16} height={16} />
                <Typography ml={1}>Delete</Typography>
              </Box>
            </MenuItem>
          </FButtonDropdown>
        ) : (
          <></>
        )}
      </TableCell>
    </TableRow>
  );
};

export default TableRowView;
